/* eslint-disable no-nested-ternary */
/* eslint-disable no-cond-assign */
/* eslint-disable @typescript-eslint/naming-convention */
import { Fragment, ReactNode } from 'react';
import * as Yup from 'yup';
import { FieldInputProps, FormikContextType } from 'formik';
import {
  ElementContent,
  ElementContentType,
  ElementStyleType,
  ElementType,
  ElementVisibility,
  UIElementInfo,
} from '../../models/parcelCreationFlowNavigation';
import { ElementBlock } from './elements.styles';
import FormFieldText from '../../common/form-field-text/form-field-text.component';
import FormSelectField from '../../common/select/form-select.component';
import { toddmmYYYY } from '../generic/generic.utils';
import { InputDateValidationConfig } from './input-date.config';
import FormCheckbox from '../../common/checkbox/checkbox-form.component';
import { LinkSmall } from '../../common/typography/typography.styles';
import { ReactComponent as InfoIcon } from '../../assets/info-blue.svg';
import { HintElement } from './styles';
import { ElementValidation } from '../../models/countriesModel';

export function prepareInitialValues<T = string | null>(
  elements: UIElementInfo[] | undefined,
  map: (v: string | null) => T = (v) => v as unknown as T
): Record<string, T> {
  if (!elements) return {};

  const initValues = elements.reduce((res, current) => {
    if (!current.behavior?.form_field) return res;
    const { form_field, selected_value } = current.behavior;
    const isDateType = current.type === 'input-date';
    return {
      ...res,
      [form_field]: isDateType
        ? toddmmYYYY(selected_value)
        : map(selected_value),
    };
  }, {});

  return initValues;
}

export function prepareValidationSchema(elements: UIElementInfo[] | undefined) {
  if (!elements) return {};

  const validationParts = elements.reduce((res, current) => {
    if (!current.behavior?.form_field) return res;
    const { form_field, required, frontend_validation } = current.behavior;
    const baseSchema = Yup.string().concat(
      (() => {
        const { visibility } = current;

        // Apply validation to the field only if its visibility rule is fullfilled.
        // In other words - don't validate hidden fields.
        if (isVisibilityRule(visibility)) {
          return Yup.string().when(visibility.field, {
            is: (value: string) => value === visibility.value,
            then: (fieldSchema) =>
              required
                ? fieldSchema.required('this_field_cannot_be_empty')
                : fieldSchema.notRequired(),
            otherwise: (fieldSchema) => fieldSchema.notRequired().strip(),
          });
        }

        return required
          ? Yup.string().required('this_field_cannot_be_empty')
          : Yup.string().notRequired();
      })()
    );

    const dateValidation =
      InputDateValidationConfig[current.behavior.form_field];

    if (dateValidation) {
      const schema = dateValidation.reduce((acc, cur) => cur(acc), baseSchema);
      return {
        ...res,
        [form_field]: schema,
      };
    }

    const schema = frontend_validation.reduce((acc, { regex, error }) => {
      const regexp = new RegExp(regex);
      return acc.matches(regexp, { message: error });
    }, baseSchema);
    return {
      ...res,
      [form_field]: schema,
    };
  }, {});

  return Yup.object(validationParts);
}

function isVisibilityRule(value: unknown): value is ElementVisibility {
  return !!value && Object.hasOwn(value, 'field');
}

export function prepareVisibilitySchema(
  elements: UIElementInfo[] | undefined,
  values: Record<string, string | null>
): Record<string, boolean> {
  if (!elements) return {};

  return elements.reduce(
    (acc, cur) => {
      const inputName = cur.behavior?.form_field ?? '';
      const visibilityRule = cur.visibility;

      let visible = false;

      if (isVisibilityRule(visibilityRule)) {
        visible = visibilityRule.value === values[visibilityRule.field];
      } else {
        visible = true;
      }

      return {
        ...acc,
        [inputName]: visible,
      };
    },
    {} as Record<string, boolean>
  );
}

export function prepareFormInputComponent(
  element: UIElementInfo,
  tabIndex?: number
) {
  if (!element.behavior?.form_field) return;

  switch (element.type) {
    case 'input-text':
      return prepareInputTextComponent(element, tabIndex);
    case 'input-date':
      return prepareInputDateComponent(element, tabIndex);
    case 'input-select':
      return prepareInputSelectComponent(element, tabIndex);
    default:
      break;
  }
}

function prepareInputSelectComponent(
  element: UIElementInfo,
  tabIndex?: number
) {
  const { type, behavior } = element;

  if (!behavior || !behavior.form_field) return;
  if (type !== 'input-select')
    throw createElementTypeError(type, 'input-select');

  const items = behavior.allowed_values?.map((x) => ({
    label: x.value,
    value: x.form_value,
  }));

  return (
    <FormSelectField
      tabIndex={tabIndex}
      name={behavior.form_field}
      key={behavior.form_field} // key is very important! it is used to map with visibility schema!
      title={behavior.title}
      items={items}
      placeholder={behavior.placeholder ?? ''}
      shouldMatchWidthOfInput
      width='100%'
      dropdownPosition='bottom-center'
    />
  );
}

function prepareInputTextComponent(element: UIElementInfo, tabIndex?: number) {
  const { type, behavior } = element;

  if (!behavior || !behavior.form_field) return;
  if (type !== 'input-text') throw createElementTypeError(type, 'input-text');

  const { frontend_validation } = behavior;

  const isNumeric = frontend_validation?.at(0)?.regex.includes('\\d');
  const maxLength = extractMaxLen(frontend_validation);

  const inputElement = (
    <FormFieldText
      tabIndex={tabIndex}
      maxLength={maxLength}
      name={behavior.form_field}
      label={behavior.title ?? ''}
      hint={behavior.tip ?? undefined}
      isOptional={!behavior.required}
      placeholder={behavior.placeholder ?? ''}
      inputMode={isNumeric ? 'numeric' : 'text'}
      validationMethod='on-submit-always'
      showMaxLengthCounter
    />
  );

  // key is very important! it is used to map with visibility schema!
  return <Fragment key={behavior.form_field}>{inputElement}</Fragment>;
}

function prepareInputDateComponent(element: UIElementInfo, tabIndex?: number) {
  const { type, behavior } = element;

  if (!behavior || !behavior.form_field) return;
  if (type !== 'input-date') throw createElementTypeError(type, 'input-date');

  const inputElement = (
    <FormFieldText
      tabIndex={tabIndex}
      name={behavior.form_field}
      label={behavior.title ?? ''}
      hint={behavior.tip ?? undefined}
      maxLength={behavior.placeholder?.length}
      isManualDateInput
      isOptional={!behavior.required}
      placeholder={behavior.placeholder ?? ''}
      validationMethod='on-submit-always'
      inputMode='text'
    />
  );

  return <Fragment key={behavior.form_field}>{inputElement}</Fragment>;
}

function prepareLink(linkContent: ElementContent) {
  if (linkContent.type !== 'link')
    throw createElementTypeError(linkContent.type, 'link');

  return (
    <LinkSmall
      key={`${linkContent.link}-${linkContent.content}`}
      underline='hover'
      href={linkContent.link}
      target='_blank'>
      {linkContent.content}
    </LinkSmall>
  );
}

function getStyleValue(elem: UIElementInfo, styleType: ElementStyleType) {
  return elem.style?.find((x) => x['style-type'] === styleType)?.value;
}

function prepareCssSize(value: string | undefined) {
  if (!value) return undefined;
  return /^\d+$/.test(value) ? `${value}px` : value;
}

function getElementBlockStyleMapping(elementBlock: UIElementInfo) {
  const elementStyleMap: Partial<
    Record<
      UIElementInfo['name'],
      {
        fontSize?: string;
        fontWeight?: number;
        padding?: string;
      }
    >
  > = {
    title: {
      fontSize: '1.2rem',
      fontWeight: 500,
      padding: '0',
    },
  };

  return elementStyleMap[elementBlock.name];
}

function prepareTextBlock(textBlock: UIElementInfo) {
  if (textBlock.type !== 'text-block')
    throw createElementTypeError(textBlock.type, 'text-block');

  return textBlock.content?.map((x) =>
    x.type === 'link' ? prepareLink(x) : x.content
  );
}

function isCheckboxType(type: ElementType) {
  return type === 'input-checkbox' || type === 'input-checkbox-select-all';
}

interface PrepareElementBlockOptions<T> {
  handleChecked?: (
    field: FieldInputProps<boolean>,
    formikContext: FormikContextType<T>
  ) => void;
  tabIndex?: number;
  keyPrefix?: string;
}

function prepareCheckboxElement<T>(
  checkboxEl: UIElementInfo,
  children: ReactNode,
  options: PrepareElementBlockOptions<T>
): ReactNode {
  if (!isCheckboxType(checkboxEl.type))
    throw createElementTypeError(checkboxEl.type, [
      'input-checkbox',
      'input-checkbox-select-all',
    ]);

  if (!checkboxEl.behavior?.form_field) return undefined;

  const { form_field, title } = checkboxEl.behavior;

  return (
    <FormCheckbox
      key={form_field}
      name={form_field}
      validationMethod='on-submit'
      tabIndex={options.tabIndex}
      onChecked={options.handleChecked}>
      {children || title}
    </FormCheckbox>
  );
}

function prepareChildren<T>(
  elements: UIElementInfo[] | undefined,
  options: PrepareElementBlockOptions<T>
): ReactNode[] | undefined {
  return elements?.map((element, index) =>
    prepareElementBlock(element, {
      ...options,
      keyPrefix: `${options.keyPrefix ?? 'element'}-${element.name}-${index}`,
    })
  );
}

function prepareRowBlock<T>(
  rowBlock: UIElementInfo,
  options: PrepareElementBlockOptions<T>
): ReactNode {
  if (rowBlock.type !== 'row-block')
    throw createElementTypeError(rowBlock.type, 'row-block');

  const checkboxElement = rowBlock.elements?.find((x) =>
    isCheckboxType(x.type)
  );

  if (checkboxElement) {
    const checkboxChildren: ReactNode[] | undefined = rowBlock.elements
      ?.filter((x) => x !== checkboxElement)
      .map((element, index) =>
        prepareElementBlock(element, {
          ...options,
          keyPrefix: `${options.keyPrefix ?? rowBlock.name}-checkbox-label-${index}`,
        })
      );

    return prepareCheckboxElement(checkboxElement, checkboxChildren, options);
  }

  return prepareStyledElementBlock(
    rowBlock,
    prepareChildren(rowBlock.elements, options),
    options.keyPrefix
  );
}

function prepareStyledElementBlock(
  elementBlock: UIElementInfo,
  children?: ReactNode,
  key?: string
): ReactNode {
  const styleMapping = getElementBlockStyleMapping(elementBlock);

  return (
    <ElementBlock
      key={key ?? elementBlock.name}
      $direction={elementBlock.type === 'row-block' ? 'row' : 'column'}
      $bgColor={getStyleValue(elementBlock, 'background-color')}
      $borderRadius={prepareCssSize(
        getStyleValue(elementBlock, 'background-rounding')
      )}
      $textColor={getStyleValue(elementBlock, 'text-color')}
      $fontSize={styleMapping?.fontSize}
      $fontWeight={styleMapping?.fontWeight}
      $padding={styleMapping?.padding}>
      {children}
    </ElementBlock>
  );
}

export function prepareElementBlock<T>(
  elementBlock: UIElementInfo | undefined,
  options: PrepareElementBlockOptions<T> = {}
): ReactNode {
  if (!elementBlock) return undefined;

  switch (elementBlock.type) {
    case 'view-block':
    case 'view_block':
    case 'column-block':
      return prepareStyledElementBlock(
        elementBlock,
        prepareChildren(elementBlock.elements, options),
        options.keyPrefix
      );
    case 'row-block':
      return prepareRowBlock(elementBlock, options);
    case 'text-block':
      return (
        <Fragment key={options.keyPrefix ?? elementBlock.name}>
          {prepareTextBlock(elementBlock)}
        </Fragment>
      );
    case 'input-checkbox':
    case 'input-checkbox-select-all':
      return prepareCheckboxElement(elementBlock, null, options);
    default:
      return undefined;
  }
}

function createElementTypeError(
  actual: ElementType | ElementContentType,
  expected:
    | ElementType
    | ElementType[]
    | ElementContentType
    | ElementContentType[]
) {
  return new Error(
    `Incorrect element type. Expecting ${Array.isArray(expected) ? expected.join(', ') : expected} but got ${actual}`
  );
}

export function extractMaxLen(
  validations: ElementValidation[]
): number | undefined {
  const quantifierRe = /\{(\d+)(?:,(\d+)?)?\}/; // {n}, {n,m}, {n,}

  const src = validations
    .map((x) => String(x.regex).replace(/^\/|\/[gimsuy]*$/g, ''))
    .find((x) => quantifierRe.test(x));

  if (!src) return;

  const m: RegExpExecArray | null = quantifierRe.exec(src);
  if (m?.at(2)) {
    return parseInt(m[2], 10);
  }

  if (m?.at(1)) {
    return parseInt(m[1], 10);
  }
}

export function prepareHint(hintElements: ElementContent[]) {
  return (
    <HintElement>
      <InfoIcon />
      <div>
        {hintElements.map((x) =>
          x.type === 'text' ? (
            <span className='hintText'>{x.content}</span>
          ) : (
            prepareLink(x)
          )
        )}
      </div>
    </HintElement>
  );
}
